/** Портал разработчика (app store 2.3): host-роутинг.
 *
 * Запросы с dev-хоста переписываются в раздел /dev того же приложения;
 * на chat-домене раздел доступен напрямую по /dev (удобно до DNS).
 * Машиночитаемые файлы документации отдаются статикой по коротким путям.
 */
import { getConfig, initConfig } from "@/lib/config";

const DEV_HOSTS = new Set(["dev.meridianhub.ru"]);

const STATIC_DOCS: Record<string, string> = {
  "/llms.txt": "/appstore/llms.txt",
  "/llms-full.txt": "/appstore/llms-full.txt",
  "/openapi.json": "/appstore/openapi.json",
  "/skill.md": "/appstore/skill.md",
};

/** Адрес портала разработчика для ссылки из чата.
 *
 * Прод: чат живёт на chat.meridianhub.ru, портал — на dev.meridianhub.ru.
 * Хардкода нет: если текущий origin — известный чат-домен, подставляем
 * парный dev-хост; на любом другом стенде портал доступен по /dev того
 * же origin (middleware этого не мешает).
 */
const CHAT_TO_DEV_HOST: Record<string, string> = {
  "chat.meridianhub.ru": "dev.meridianhub.ru",
};

/** Прод-адрес портала для сред без web-origin: десктоп-рендерер живёт на
 * file://, и относительный «/dev» там превращался в мёртвый file:///dev —
 * пункт меню «Разработчикам» молча не открывался (жалоба 22.09). */
const FALLBACK_DEV_PORTAL_URL = "https://dev.meridianhub.ru/dev";

export function devPortalUrl(origin: string): string {
  try {
    const url = new URL(origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return FALLBACK_DEV_PORTAL_URL;
    }
    const paired = CHAT_TO_DEV_HOST[url.hostname.toLowerCase()];
    if (paired) return `${url.protocol}//${paired}/dev`;
    return `${url.origin}/dev`;
  } catch {
    return FALLBACK_DEV_PORTAL_URL;
  }
}

/** Адрес страницы подключения приложения: она живёт на chat-домене
 * (портал — на dev-домене, его origin не годится). */
export function appConnectUrl(token: string): string {
  return `https://chat.meridianhub.ru/app/${token}`;
}

export function isDevPortalHost(host: string): boolean {
  return DEV_HOSTS.has(host.split(":")[0].toLowerCase());
}

/** Куда переписать путь для данного хоста; null — не трогать. */
export function resolveDevRewrite(host: string, pathname: string): string | null {
  if (!isDevPortalHost(host)) return null;
  if (STATIC_DOCS[pathname]) return STATIC_DOCS[pathname];
  if (
    pathname === "/dev" ||
    pathname.startsWith("/dev/") ||
    // Возврат SSO: /auth/callback обязан работать на dev-хосте как есть —
    // токены кладутся в localStorage ЭТОГО origin.
    pathname.startsWith("/auth/") ||
    pathname.startsWith("/_next") ||
    pathname.startsWith("/appstore/") ||
    pathname.startsWith("/api/")
  ) {
    return null;
  }
  return pathname === "/" ? "/dev" : `/dev${pathname}`;
}

/** Куда идти после /auth/callback. На dev-хосте чатовые пути (/chats)
 * не существуют — идём в корень портала; но путь самого портала сервер
 * теперь сохраняет (жалоба 22.09), и его затирать нельзя. */
export function resolveCallbackNext(host: string, next: string): string {
  if (!isDevPortalHost(host)) return next;
  return next === "/dev" || next.startsWith("/dev/") ? next : "/";
}

/** st2-state существующего OAuth-флоу — клиентская сборка, симметричная
 * серверному декодеру (chat auth_router.encode_sso_state): "st2." +
 * b64url(JSON {invite, redirect_target}) без хвостовых "=". */
export function encodeSsoState(redirectTarget: string, invite = ""): string {
  const json = JSON.stringify({ invite, redirect_target: redirectTarget });
  const b64 = btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  return `st2.${b64}`;
}

/** Подтягивает публичный конфиг, если в сторе нет oauthClientId.
 *
 * Прод-баг: стор наполняет use-app-init (инициализация ЧАТ-приложения),
 * на портале она не выполняется — ссылка входа уходила на passport с
 * пустым client_id. Здесь тот же запрос, что в use-app-init.
 * Бросает, если client_id так и не появился — редиректить в пустоту
 * нельзя. Импорт chat ленивый: dev-host используется и в middleware.
 */
export async function ensureOAuthConfig(): Promise<string> {
  const current = getConfig();
  if (current.oauthClientId) return current.oauthClientId;

  const { chat } = await import("@/lib/http");
  const cfg = await chat<Record<string, string>>("/v1/chat/config", {
    method: "GET",
  });
  const next = {
    ...current,
    oauthClientId: cfg.oauth_client_id || current.oauthClientId,
    passportWebUrl: cfg.passport_web_url || current.passportWebUrl,
    hcaptchaSiteKey: cfg.hcaptcha_site_key || current.hcaptchaSiteKey,
  };
  initConfig(next);
  if (!next.oauthClientId) {
    throw new Error("oauth_client_id is not configured");
  }
  return next.oauthClientId;
}

/** Ссылка входа с гарантированным конфигом — единственный путь для
 * авто-редиректа гостя и кнопки «Войти». */
export async function resolveDevLoginUrl(target: string): Promise<string> {
  await ensureOAuthConfig();
  return buildDevLoginUrl(target);
}

/** Гость на dev-портале → Passport-флоу с возвратом на dev-origin.
 * Импорт конфига — статический: модуль без side-effects, middleware
 * (использует только resolveDevRewrite) он не ломает. */
export function buildDevLoginUrl(target: string): string {
  // target — полный адрес страницы (origin + путь): сервер вернёт после
  // логина именно на неё, а не в /chats (жалоба 22.09).
  const config = getConfig();
  const url = new URL(`${config.passportWebUrl.replace(/\/$/, "")}/oauth/authorize`);
  url.searchParams.set("client_id", config.oauthClientId);
  url.searchParams.set(
    "redirect_uri",
    `${config.chatUrl.replace(/\/$/, "")}/v1/chat/auth/callback`,
  );
  url.searchParams.set("state", encodeSsoState(target));
  return url.toString();
}
